import React, { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import Form from "antd/es/form";
import { useForm } from "antd/es/form/Form";
import Tooltip from "antd/es/tooltip";
import { useIntl } from "react-intl";

import { Company, Deal, EntityType, Person } from "@mapmycustomers/shared/types/entity";
import Funnel from "@mapmycustomers/shared/types/entity/deals/Funnel";
import Stage from "@mapmycustomers/shared/types/entity/deals/Stage";
import FormLayout, { LayoutSchemaField } from "@mapmycustomers/shared/types/layout/FormLayout";
import { isDefined } from "@mapmycustomers/shared/util/assert";
import { Button, LoadingSpinner } from "@mapmycustomers/ui";

import {
  getAssociationsState,
  isCreating,
  isEntityRelating,
  isEntityRelatingSuccessful,
  isFilesDeleting,
  isGlobalAddDeal,
  isInitializing,
} from "@app/component/createEditEntity/CreateDealModal/store";
import {
  changeAssociatedEntities,
  clearAllUploadedDealFiles,
  createDeal,
  initialize,
  relateUnrelateEntities,
} from "@app/component/createEditEntity/CreateDealModal/store/actions";
import convertFieldToLayoutSchemaField from "@app/component/FormFields/utils/convertFieldToLayoutSchemaField";
import FormLayoutSelector from "@app/component/FormLayoutSelector";
import FormNoLayoutAlert from "@app/component/FormNoLayoutAlert";
import EntityModal from "@app/component/modal/EntityModal";
import { showGlobalPostCreationModal } from "@app/component/PostCreationModal/store/actions";
import PreviewModeIndicator from "@app/component/PreviewModeIndicator";
import { UpdateDealPayload } from "@app/store/api/ApiService";
import { getFunnelStages } from "@app/store/deal";
import { getCurrentUserId } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import { AnalyticsService } from "@app/util/analytic/AnalyticsService";
import AnalyticsContext from "@app/util/contexts/AnalyticsContext";
import { DealFieldName } from "@app/util/fieldModel/DealFieldModel";
import getFieldModelByEntityType from "@app/util/fieldModel/getByEntityType";
import { formatDate } from "@app/util/formatters";
import useFormChangeTracker from "@app/util/hook/form/useFormChangeTracker";
import useErroredFieldsScroller from "@app/util/hook/useErroredFieldsScroller";
import useFieldValidation from "@app/util/hook/useFieldValidation";
import { dealLayoutModel } from "@app/util/layout/impl";

import RelatedEntitiesMatching from "../../RelatedEntitiesMatching";
import { dealAnalytics } from "../analytics";

import DealFormFields, { FunnelStageValue } from "./DealFormFields";
import FormValues from "./types/FormValues";

interface CreateDealModalProps {
  analyticIssuer?: AnalyticsService;
  className?: string;
  dealStageExtra?: ReactNode;
  defaultClosingDate?: Date;
  defaultFunnelId?: Funnel["id"];
  defaultStageId?: Stage["id"];
  fixedCompany?: Company;
  fixedPerson?: Person;
  okText?: ReactNode;
  onHide: (newDeal?: Deal) => void;
  previewLayout?: FormLayout;
}

const CreateDealModal = ({
  analyticIssuer,
  associations,
  className,
  clearAllUploadedFiles,
  createDeal,
  creating,
  currentUserId,
  dealStageExtra,
  defaultClosingDate,
  defaultFunnelId,
  defaultStageId,
  entityRelatingSuccess,
  fixedCompany,
  fixedPerson,
  funnelStages,
  initialize,
  initializing,
  isEntityRelating,
  isFilesDeleting,
  isGlobalAddDeal,
  okText,
  onChangeAssociatedEntities,
  onHide,
  onShowGlobalPostCreationModal,
  previewLayout,
  relateUnrelateEntities,
}: CreateDealModalProps & ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps) => {
  const intl = useIntl();

  const [formLayout, setFormLayout] = useState(dealLayoutModel.defaultFormLayout);
  const [formContainer, handleFinishFail] = useErroredFieldsScroller();

  useEffect(() => {
    initialize({ fixedCompany, fixedPerson });
  }, [fixedCompany, fixedPerson, initialize]);

  const [form] = useForm<FormValues>();
  const [isFormChanged, handleFormChanges] = useFormChangeTracker(form);

  const defaultDealName = useMemo(() => {
    const parentName = fixedCompany?.name ?? fixedPerson?.name;
    return parentName
      ? intl.formatMessage(
          {
            id: "createDealModal.name.defaultName",
            defaultMessage: "{parentName}: New Deal",
          },
          { parentName }
        )
      : "";
  }, [intl, fixedCompany, fixedPerson]);

  const handleCancelClick = useCallback(() => {
    clearAllUploadedFiles();
    onHide();
  }, [clearAllUploadedFiles, onHide]);
  const handleOkClick = useCallback(() => form.submit(), [form]);

  const handleVisibility = useCallback(
    (deal?: Deal) => {
      if (isGlobalAddDeal && deal) {
        onShowGlobalPostCreationModal({ entity: deal, entityType: EntityType.DEAL });
      }
      onHide(deal);
    },
    [isGlobalAddDeal, onHide, onShowGlobalPostCreationModal]
  );

  const handleFieldValidationErrors = useFieldValidation(form, handleFinishFail, true);

  const handleCreateDeal = useCallback(() => {
    const layoutId = formLayout?.id;

    if (layoutId) {
      const values = form.getFieldsValue();

      const deal: UpdateDealPayload = {
        account: values.companyId ? { id: values.companyId } : undefined,
        amount: values.amount,
        cadenceInterval: values.cadenceInterval,
        closingDate: values.closingDate ? formatDate(values.closingDate, "yyyy-MM-dd") : null,
        contact: values.personId ? { id: values.personId } : undefined,
        crmActivityTypeId: values.crmActivityTypeId,
        dealLossComment: values.dealLossComment,
        dealLossReason: values.dealLossReasonId ? { id: values.dealLossReasonId } : undefined,
        funnel: values.funnelId ? { id: values.funnelId } : undefined,
        name: values.name,
        score: values.score,
        stage: values.stageId ? { id: values.stageId } : undefined,
        user: values.user ? { id: values.user } : undefined,
      };

      createDeal({
        callback: handleVisibility,
        customFieldsValues: values.customFields
          ? Object.values(values.customFields).filter(isDefined)
          : [],
        deal,
        fieldsValueValidateCallback: handleFieldValidationErrors,
        groupsIdsToAdd: values.groups ? Array.from(values.groups) : [],
        layoutId,
      });

      analyticIssuer?.feature(["Deal Created"], { dealId: deal.id, dealName: deal.name });
    }
  }, [
    formLayout?.id,
    form,
    createDeal,
    handleVisibility,
    handleFieldValidationErrors,
    analyticIssuer,
  ]);

  const handleFormFinish = useCallback(() => {
    handleCreateDeal();
  }, [handleCreateDeal]);

  const isPreviewMode = !!previewLayout;

  const currentLayout = isPreviewMode ? previewLayout : formLayout;

  const submitButtonDisabled =
    creating || initializing || isFilesDeleting || isPreviewMode || !currentLayout;

  const funnelStageDefaultValues = useMemo(() => {
    const values: FunnelStageValue = {};

    if (defaultFunnelId) {
      return { funnelId: defaultFunnelId, stageId: defaultStageId };
    }

    currentLayout?.schema.forEach((schemaField) => {
      if (schemaField.field === "funnelId") {
        values.funnelId = schemaField.defaultValue?.[0] as Funnel["id"];
      } else if (schemaField.field === "dealStageId") {
        const defaultStageId = schemaField.defaultValue?.[0] as Stage["id"];
        const funnelIdString = Object.keys(funnelStages).find((funnelId) =>
          funnelStages[parseInt(funnelId) as Funnel["id"]].some(({ id }) => id === defaultStageId)
        );
        if (defaultStageId && funnelIdString) {
          values.stageId = defaultStageId;
          values.funnelId = parseInt(funnelIdString);
        }
      }
    });
    return values;
  }, [currentLayout, defaultFunnelId, defaultStageId, funnelStages]);

  const defaultAddedFields = useMemo(() => {
    const fieldModel = getFieldModelByEntityType(EntityType.DEAL);
    const fieldNamesToAdd: DealFieldName[] = [];

    if (fixedCompany) {
      fieldNamesToAdd.push(DealFieldName.COMPANY);
    }
    if (fixedPerson) {
      fieldNamesToAdd.push(DealFieldName.PERSON);
    }
    if (defaultClosingDate) {
      fieldNamesToAdd.push(DealFieldName.CLOSING_DATE);
    }

    return fieldNamesToAdd.reduce((schemaField: LayoutSchemaField[], name) => {
      const field = fieldModel.getByName(name);
      if (field) {
        schemaField.push(convertFieldToLayoutSchemaField(field));
      }
      return schemaField;
    }, []);
  }, [defaultClosingDate, fixedCompany, fixedPerson]);

  return (
    <AnalyticsContext.Provider value={dealAnalytics}>
      <EntityModal
        className={className}
        entityTypeName="Deal"
        footer={
          <>
            <Tooltip
              placement="topRight"
              title={
                isPreviewMode
                  ? intl.formatMessage({
                      id: "createDealModal.footer.previewMode.tooltip",
                      defaultMessage: "Please exit Preview Mode to create deal",
                      description: "Create Deal modal - OK button tooltip in preview mode",
                    })
                  : null
              }
              trigger={isPreviewMode ? ["hover"] : []}
            >
              <Button
                disabled={submitButtonDisabled}
                loading={creating}
                onClick={handleOkClick}
                type="primary"
              >
                {okText ??
                  intl.formatMessage({
                    id: "createDealModal.footer.createDeal",
                    defaultMessage: "Create Deal",
                    description: "Create button title on Create Deal modal",
                  })}
              </Button>
            </Tooltip>
          </>
        }
        onCancel={handleCancelClick}
        onOk={handleOkClick}
        showConfirm={!isPreviewMode && isFormChanged}
        title={
          <>
            {intl.formatMessage({
              id: "createDealModal.header",
              defaultMessage: "Create Deal",
              description: "Header of Create Deal modal",
            })}

            {isPreviewMode ? (
              <PreviewModeIndicator />
            ) : (
              <FormLayoutSelector
                disabled={creating || initializing}
                entityType={EntityType.DEAL}
                layout={formLayout}
                onChange={setFormLayout}
              />
            )}
          </>
        }
        titleExtra={
          <RelatedEntitiesMatching
            associatedCompany={associations.associatedCompany}
            associatedPerson={associations.associatedPerson}
            entityRelatingSuccess={entityRelatingSuccess}
            isEntityRelating={isEntityRelating}
            relateUnrelateEntities={relateUnrelateEntities}
          />
        }
      >
        {initializing ? (
          <LoadingSpinner />
        ) : currentLayout ? (
          <div ref={formContainer}>
            <Form
              form={form}
              initialValues={{
                ...funnelStageDefaultValues,
                cadenceInterval: null,
                closingDate: defaultClosingDate,
                companyId: fixedCompany?.id,
                name: defaultDealName,
                personId: fixedPerson?.id,
                user: currentUserId,
              }}
              layout="vertical"
              onFinish={handleFormFinish}
              onFinishFailed={handleFinishFail}
              onValuesChange={handleFormChanges}
              preserve
            >
              <DealFormFields
                associations={associations}
                dealStageExtra={dealStageExtra}
                defaultAddedFields={defaultAddedFields}
                fixedCompany={fixedCompany}
                fixedPerson={fixedPerson}
                funnelStageDefaultValues={funnelStageDefaultValues}
                layout={currentLayout}
                onChangeAssociatedEntities={onChangeAssociatedEntities}
                relateUnrelateEntities={relateUnrelateEntities}
              />
            </Form>
          </div>
        ) : (
          <FormNoLayoutAlert />
        )}
      </EntityModal>
    </AnalyticsContext.Provider>
  );
};

const mapStateToProps = (state: RootState) => ({
  associations: getAssociationsState(state),
  creating: isCreating(state),
  currentUserId: getCurrentUserId(state)!,
  entityRelatingSuccess: isEntityRelatingSuccessful(state),
  funnelStages: getFunnelStages(state),
  initializing: isInitializing(state),
  isEntityRelating: isEntityRelating(state),
  isFilesDeleting: isFilesDeleting(state),
  isGlobalAddDeal: isGlobalAddDeal(state),
});

const mapDispatchToProps = {
  clearAllUploadedFiles: clearAllUploadedDealFiles,
  createDeal: createDeal.request,
  initialize: initialize.request,
  onChangeAssociatedEntities: changeAssociatedEntities.request,
  onShowGlobalPostCreationModal: showGlobalPostCreationModal,
  relateUnrelateEntities: relateUnrelateEntities.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(CreateDealModal);
